import { CreateUserListInput, ExpertType } from '#app/__generated__/api.js';
import {
  UpdateUsersMembershipsInUserListsInput,
  useAddToUserListUpdateMembershipsMutation,
} from '#app/__generated__/relay/useAddToUserListUpdateMembershipsMutation.graphql.js';
import { ContraImage } from '#app/components/Avatars/Avatar/types.js';
import { AddToUserListsVariants } from '#app/components/Modals/V2/GlobalModalSystem/templates/AddToUserListsModal.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useAdvancedNetworkSubscriptionFlow } from '#app/hooks/useAdvancedNetworkSubscriptionFlow.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useFeatureFlag } from '#app/hooks/useFeatureFlag.js';
import { useUserListMutations } from './useUserListMutations.js';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type OnListUpdatedArgs = {
  readonly listIdsAddedTo: readonly string[];
  readonly updatedLists: ReadonlyArray<{
    readonly expertProgram: null | {
      readonly expertPartnership: ExpertType;
    };
    readonly id: string;
  }>;
};

type UseAddToUserListProps = {
  readonly addContact: (args: { onSuccess?: () => void }) => void;
  readonly onListCreated?: (createdListId: string) => void;
  readonly onListUpdated?: (args: OnListUpdatedArgs) => void;
  readonly removeContact: (args: {
    onSuccess?: (allListIds: string[]) => void;
  }) => void;
  readonly shouldShowCreateListScreenIfNoneExist?: boolean;
  readonly variant?: AddToUserListsVariants;
};

type UserProfileInfo = {
  readonly avatarImage: ContraImage | null;
  readonly contactId: null | string;
  readonly fullName: string;
  readonly id: string;
  readonly isSavedContact: boolean;
};

export const useAddToUserList = ({
  addContact,
  onListCreated,
  onListUpdated,
  removeContact,
  shouldShowCreateListScreenIfNoneExist = false,
  variant = 'DEFAULT',
}: UseAddToUserListProps) => {
  const [interactedUserProfiles, setInteractedUserProfiles] = useState<
    null | UserProfileInfo[]
  >(null);
  const [offPlatformContactCount, setOffPlatformContactCount] =
    useState<number>(0);
  const membersOnly = useFeatureFlag('membersOnly');

  const toast = useToast();

  const { createUserList, isCreatingNewList } = useUserListMutations();

  const handleCreateUserList = useCallback(
    ({
      input,
      onSuccess,
    }: {
      input: CreateUserListInput;
      onSuccess: (createdListId: string) => void;
    }) => {
      createUserList({
        name: input.displayName,
        onSuccess: (createdListId) => {
          onSuccess(createdListId);
          onListCreated?.(createdListId);
        },
      });
    },
    [createUserList, onListCreated],
  );

  const [updateListMemberships, isUpdatingListMemberships] =
    useContraMutation<useAddToUserListUpdateMembershipsMutation>(graphql`
      mutation useAddToUserListUpdateMembershipsMutation(
        $input: UpdateUsersMembershipsInUserListsInput!
      ) {
        updateUsersMembershipsInUserLists(input: $input) {
          errors {
            __typename
            message
          }
          visitor {
            userAccount {
              ...NetworkTableV2_userLists_userAccount
              ...AddToUserListsModal_userAccount
              workspaceForSelectedUserType {
                ... on ClientWorkspace {
                  __typename
                  userLists(first: 200, filter: { groupNid: NETWORK_LIST }) {
                    edges {
                      node {
                        id
                        expertProgram {
                          expertPartnership
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `);

  const handleUpdateListMemberships = useCallback(
    ({
      input,
      onSuccess,
    }: {
      input: UpdateUsersMembershipsInUserListsInput;
      onSuccess: () => void;
    }) => {
      updateListMemberships({
        onCompleted: (errors, response) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              captureException(payloadErrors[0]);
              toast.error(
                'Sorry, something went wrong. Please try again or get in touch with support for assistance.',
              );
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            onSuccess();

            const updatedLists =
              response?.updateUsersMembershipsInUserLists?.visitor?.userAccount
                ?.workspaceForSelectedUserType?.__typename === 'ClientWorkspace'
                ? response?.updateUsersMembershipsInUserLists?.visitor?.userAccount?.workspaceForSelectedUserType?.userLists?.edges?.map(
                    (edge) => edge?.node,
                  )
                : [];

            onListUpdated?.({
              listIdsAddedTo: input.userListIdsToAddTo,
              updatedLists,
            });
          }
        },
        onError: (error) => {
          captureException(error);
        },
        variables: {
          input,
        },
      });
    },
    [onListUpdated, toast, updateListMemberships],
  );

  // we do this because there would otherwise be a dependency cycle between the add to list modal and the subscription flow hook callbacks.
  const startRef = useRef<(() => void) | null>(null);

  const { track } = useAnalytics();
  const handleUpgrade = useCallback(() => {
    if (!startRef.current) {
      return;
    }

    track('button', {
      action: 'tapped',
      button_name: membersOnly
        ? 'request_membership'
        : 'upgrade_to_advanced_network',
      section: 'add_to_user_lists_modal',
    });

    startRef.current();
  }, [membersOnly, track]);

  const { closeModal, openModal: openUpdateUserListMembershipModal } = useModal(
    {
      identifier: 'AddToUserListsModal',
      modalProps: {
        handleUpgrade,
        isCreatingNewList,
        isUpdatingListMemberships,
        offPlatformContactCount,
        onAddContact: addContact,
        onCreateNewList: handleCreateUserList,
        onRemoveContact: removeContact,
        onUpdateListMemberships: handleUpdateListMemberships,
        shouldShowCreateListScreenIfNoneExist,
        updatingUserProfiles: interactedUserProfiles
          ? interactedUserProfiles
          : [],
        variant,
      },
    },
  );

  const { start: startSubscriptionFlow } = useAdvancedNetworkSubscriptionFlow({
    onSubscribed: () => {
      openUpdateUserListMembershipModal();
    },
    onSubscriptionModalClosed: () => {
      openUpdateUserListMembershipModal();
    },
    onUpsellModalClosed: () => {
      openUpdateUserListMembershipModal();
    },
  });

  useEffect(() => {
    if (startRef.current !== startSubscriptionFlow) {
      startRef.current = startSubscriptionFlow;
    }
  }, [startSubscriptionFlow]);

  const handleOpenAddToListModal = useCallback(
    ({
      numberOfContactsWithoutProfile,
      userProfiles,
    }: {
      numberOfContactsWithoutProfile?: number;
      userProfiles: UserProfileInfo[];
    }) => {
      setInteractedUserProfiles(userProfiles);
      setOffPlatformContactCount(numberOfContactsWithoutProfile ?? 0);

      openUpdateUserListMembershipModal();
    },
    [openUpdateUserListMembershipModal],
  );

  return {
    closeModal,
    handleOpenAddToListModal,
    handleUpdateListMemberships,
  };
};
